import { useState } from "react";
import { Select } from "antd";
import { SubscriptionItem, SubscriptionCreate } from "../../../../services/subscription";
import RIcon from "../../../../components/RIcon";

type ScheduleType = "once" | "daily" | "weekly" | "custom";

interface SubscriptionModalProps {
  initial?: SubscriptionItem | null;
  onClose: () => void;
  onSave: (data: SubscriptionCreate) => void;
}

const WEEKDAYS = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];

export default function SubscriptionModal({ initial, onClose, onSave }: SubscriptionModalProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [websiteInput, setWebsiteInput] = useState("");
  const [websites, setWebsites] = useState<string[]>(initial?.websites ?? []);
  const [kwInput, setKwInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>(initial?.keywords ?? []);
  const [scheduleType, setScheduleType] = useState<ScheduleType>((initial?.schedule_type as ScheduleType) ?? "daily");
  const [scheduleTime, setScheduleTime] = useState(initial?.schedule_time ?? "08:00");
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState(initial?.schedule_day_of_week ?? 1);
  const [cronExpression, setCronExpression] = useState(initial?.cron_expression ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const addWebsite = () => {
    const v = websiteInput.trim().replace(/^https?:\/\//, "");
    if (v && !websites.includes(v)) { setWebsites([...websites, v]); }
    setWebsiteInput("");
  };

  const addKeyword = () => {
    const v = kwInput.trim();
    if (v && !keywords.includes(v)) { setKeywords([...keywords, v]); }
    setKwInput("");
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "请填写订阅名称";
    if (websites.length === 0) errs.websites = "请至少添加一个目标网站";
    if (keywords.length === 0) errs.keywords = "请至少添加一个关键词";
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    const payload: SubscriptionCreate = {
      name: name.trim(),
      description: description.trim() || undefined,
      websites,
      keywords,
    };

    payload.schedule_type = scheduleType;
    if (scheduleType === "daily" || scheduleType === "weekly") {
      payload.schedule_time = scheduleTime;
    }
    if (scheduleType === "weekly") {
      payload.schedule_day_of_week = scheduleDayOfWeek;
    }
    if (scheduleType === "custom" && cronExpression.trim()) {
      payload.cron_expression = cronExpression.trim();
    }

    onSave(payload);
  };

  const inputCls = "w-full border rounded-lg px-3 h-9 text-sm outline-none focus:border-[#0EA5A0] transition-colors border-gray-200 bg-white";
  const labelCls = "block text-xs font-medium text-[#4A5568] mb-1";
  const sectionCls = "border border-gray-100 rounded-xl p-4 space-y-3";
  const sectionTitle = "text-sm font-semibold text-[#1A2B3C] flex items-center gap-2";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <h3 className="text-base font-semibold text-[#1A2B3C]">
            {initial ? "编辑订阅" : "新建内容订阅"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
            <RIcon name="close-line" className="text-[#718096]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* 基本信息 */}
          <div className={sectionCls}>
            <div className={sectionTitle}>
              <div className="w-5 h-5 flex items-center justify-center"><RIcon name="information-line" className="text-[#0EA5A0]" /></div>
              基本信息
            </div>
            <div>
              <label className={labelCls}>订阅名称 <span className="text-red-400">*</span></label>
              <input
                className={`${inputCls} ${errors.name ? "border-red-400" : ""}`}
                placeholder="例：医疗器械法规动态"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors(p => ({ ...p, name: "" })); }}
              />
              {errors.name && <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>}
            </div>
            <div>
              <label className={labelCls}>描述（可选）</label>
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0EA5A0] transition-colors resize-none"
                rows={2}
                placeholder="简要描述此订阅的用途..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>

          {/* 订阅目标 */}
          <div className={sectionCls}>
            <div className={sectionTitle}>
              <div className="w-5 h-5 flex items-center justify-center"><RIcon name="global-line" className="text-[#0EA5A0]" /></div>
              订阅目标
            </div>
            <div>
              <label className={labelCls}>目标网站 <span className="text-red-400">*</span></label>
              <div className="flex gap-2">
                <input
                  className={`${inputCls} flex-1 ${errors.websites ? "border-red-400" : ""}`}
                  placeholder="输入网站域名，如 www.fda.gov"
                  value={websiteInput}
                  onChange={(e) => setWebsiteInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addWebsite(); } }}
                />
                <button type="button" onClick={addWebsite} className="px-3 h-9 bg-[#0EA5A0]/10 text-[#0EA5A0] rounded-lg text-sm hover:bg-[#0EA5A0]/20 cursor-pointer whitespace-nowrap">
                  添加
                </button>
              </div>
              {errors.websites && <p className="text-red-500 text-xs mt-0.5">{errors.websites}</p>}
              {websites.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {websites.map((w) => (
                    <span key={w} className="inline-flex items-center gap-1 bg-gray-100 text-[#4A5568] text-xs px-2.5 py-1 rounded-full">
                      <div className="w-3 h-3 flex items-center justify-center"><RIcon name="global-line" /></div>
                      {w}
                      <button type="button" onClick={() => setWebsites(websites.filter(x => x !== w))} className="w-3 h-3 flex items-center justify-center text-[#718096] hover:text-red-500 cursor-pointer">
                        <RIcon name="close-line" className="text-xs" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>关键词 <span className="text-red-400">*</span></label>
              <div className="flex gap-2">
                <input
                  className={`${inputCls} flex-1 ${errors.keywords ? "border-red-400" : ""}`}
                  placeholder="输入关键词，按回车或点击添加"
                  value={kwInput}
                  onChange={(e) => setKwInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                />
                <button type="button" onClick={addKeyword} className="px-3 h-9 bg-[#0EA5A0]/10 text-[#0EA5A0] rounded-lg text-sm hover:bg-[#0EA5A0]/20 cursor-pointer whitespace-nowrap">
                  添加
                </button>
              </div>
              {errors.keywords && <p className="text-red-500 text-xs mt-0.5">{errors.keywords}</p>}
              {keywords.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {keywords.map((kw) => (
                    <span key={kw} className="inline-flex items-center gap-1 bg-[#0EA5A0]/10 text-[#0EA5A0] text-xs px-2.5 py-1 rounded-full">
                      {kw}
                      <button type="button" onClick={() => setKeywords(keywords.filter(x => x !== kw))} className="w-3 h-3 flex items-center justify-center hover:text-red-500 cursor-pointer">
                        <RIcon name="close-line" className="text-xs" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 执行策略 */}
          <div className={sectionCls}>
            <div className={sectionTitle}>
              <div className="w-5 h-5 flex items-center justify-center"><RIcon name="time-line" className="text-[#0EA5A0]" /></div>
              执行策略
            </div>
            <div className="grid grid-cols-4 gap-2">
              {([
                { value: "once", label: "单次执行" },
                { value: "daily", label: "每日定时" },
                { value: "weekly", label: "每周定时" },
                { value: "custom", label: "Cron 自定义" },
              ] as { value: ScheduleType; label: string }[]).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setScheduleType(opt.value)}
                  className={`border rounded-lg py-2 text-xs cursor-pointer transition-colors whitespace-nowrap ${scheduleType === opt.value ? "border-[#0EA5A0] bg-[#0EA5A0]/10 text-[#0EA5A0] font-medium" : "border-gray-200 text-[#4A5568] hover:border-[#0EA5A0]/40"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {(scheduleType === "daily" || scheduleType === "weekly") && (
              <div className="flex gap-3">
                {scheduleType === "weekly" && (
                  <div className="flex-1">
                    <label className={labelCls}>执行日期</label>
                    <Select
                      style={{ width: "100%" }}
                      value={scheduleDayOfWeek}
                      onChange={(v) => setScheduleDayOfWeek(Number(v))}
                      options={WEEKDAYS.map((d, i) => ({ value: i + 1, label: d }))}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <label className={labelCls}>执行时间</label>
                  <input
                    type="time"
                    className={inputCls}
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                  />
                </div>
              </div>
            )}
            {scheduleType === "custom" && (
              <div>
                <label className={labelCls}>Cron 表达式</label>
                <input
                  className={inputCls}
                  placeholder="0 8 * * * （每天08:00）"
                  value={cronExpression}
                  onChange={(e) => setCronExpression(e.target.value)}
                />
                <p className="text-xs text-[#718096] mt-0.5">格式：分 时 日 月 周</p>
              </div>
            )}
            {scheduleType === "once" && (
              <p className="text-xs text-[#718096] bg-gray-50 rounded-lg px-3 py-2">创建后立即执行一次，执行完成后不再自动触发。</p>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 shrink-0">
          <button type="button" onClick={onClose} className="flex-1 h-9 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
            className="flex-1 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            {initial ? "保存修改" : "创建订阅"}
          </button>
        </div>
      </div>
    </div>
  );
}
